import axios from "axios";
import DemoSignatureService from "../DemoSignatureService";

const API_URL = import.meta.env.VITE_API_URL;

// Función para obtener la URL de la imagen desde S3
export const fetchImageUrl = async (imagePath) => {
  try {
    if (!imagePath) {
      console.warn("fetchImageUrl: imagePath es null o undefined");
      return null;
    }

    // Verificar si es una firma local de demo
    if (DemoSignatureService.isLocalDemoSignature(imagePath)) {
      const localSignature =
        DemoSignatureService.getLocalSignatureImage(imagePath);
      console.log("🎬 PDF: Usando firma local de demo");
      return localSignature; // Retornar directamente la imagen base64
    }

    const [folder, file] = imagePath.split("/");

    const response = await axios.get(
      `${API_URL}/files/${file}?folder=${folder}`,
      { withCredentials: true },
    );
    return response.data.url;
  } catch (error) {
    console.error("Error fetching image URL:", error);
    return null;
  }
};

// Función para convertir la URL de la imagen a base64 preservando transparencia
export const convertToBase64 = (url) => {
  // Si ya es una imagen base64 (como las firmas locales), retornarla directamente
  if (
    typeof url === "string" &&
    (url.startsWith("data:image/") || url.startsWith("/static/"))
  ) {
    console.log(
      "🎬 PDF: Imagen ya en formato base64 o local, usando directamente",
    );
    return Promise.resolve(url);
  }

  return axios
    .get(url, { responseType: "blob" })
    .then((response) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result;
          // Verificar que la imagen es PNG y preservar transparencia
          if (response.data.type === "image/png") {
            resolve(base64);
          } else {
            // Si no es PNG, crear un canvas para convertir a PNG con transparencia
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              canvas.width = img.width;
              canvas.height = img.height;

              // Limpiar el canvas con transparencia
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);

              // Convertir a PNG con transparencia
              const pngBase64 = canvas.toDataURL("image/png");
              resolve(pngBase64);
            };
            img.onerror = reject;
            img.src = base64;
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(response.data);
      });
    })
    .catch((error) => {
      console.error("Error converting image to base64:", error);
      return null;
    });
};

/**
 * Renderiza una imagen manteniendo sus proporciones, dentro de un área con ancho y alto máximos.
 * Opcionalmente, se puede agregar una descripción centrada debajo.
 *
 * @param {Object} doc - Instancia del documento jsPDF.
 * @param {string} imagePath - Ruta o referencia de la imagen.
 * @param {number} x - Posición X donde se iniciará a dibujar.
 * @param {number} y - Posición Y donde se iniciará a dibujar.
 * @param {number} maxWidth - Ancho máximo disponible para la imagen (incluyendo padding).
 * @param {number} maxHeight - Alto máximo permitido para la imagen (sin incluir la descripción).
 * @param {number} [cellPadding=5] - Padding (margen interno) para la imagen.
 * @param {string} [description=""] - Texto de descripción a mostrar debajo de la imagen.
 *
 * @returns {Promise<number>} Altura total consumida (imagen + padding + descripción).
 */
export const renderImageProporcional = async (
  doc,
  imagePath,
  x,
  y,
  maxWidth,
  maxHeight,
  cellPadding = 5,
  description = "",
) => {
  // Obtener la URL y convertir la imagen a Base64
  const url = await fetchImageUrl(imagePath);
  if (!url) {
    throw new Error("No se pudo obtener la URL de la imagen");
  }

  // Si es una firma local, ya tenemos el base64
  let imageBase64;
  if (DemoSignatureService.isLocalDemoSignature(imagePath)) {
    imageBase64 = url; // Ya es base64
  } else {
    imageBase64 = await convertToBase64(url);
    if (!imageBase64) {
      throw new Error("No se pudo convertir la imagen a Base64");
    }
  }

  // Calcular dimensiones reales manteniendo la proporción de la imagen
  const dimensions = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let imgWidth, imgHeight;
      const aspectRatio = img.width / img.height;

      if (aspectRatio > 1) {
        // Imagen horizontal
        imgWidth = Math.min(maxWidth - cellPadding * 2, img.width);
        imgHeight = imgWidth / aspectRatio;
        if (imgHeight > maxHeight) {
          imgHeight = maxHeight;
          imgWidth = imgHeight * aspectRatio;
        }
      } else {
        // Imagen vertical
        imgHeight = Math.min(maxHeight, img.height);
        imgWidth = imgHeight * aspectRatio;
        if (imgWidth > maxWidth - cellPadding * 2) {
          imgWidth = maxWidth - cellPadding * 2;
          imgHeight = imgWidth / aspectRatio;
        }
      }
      resolve({ width: imgWidth, height: imgHeight });
    };

    img.onerror = () => {
      console.error("Error cargando la imagen para obtener dimensiones");
      resolve({ width: maxWidth / 2, height: maxHeight / 2 });
    };

    img.src = imageBase64;
    setTimeout(() => {
      if (!img.complete) {
        resolve({ width: maxWidth / 2, height: maxHeight / 2 });
      }
    }, 1000);
  });

  const imgWidth = dimensions.width;
  const imgHeight = dimensions.height;

  // Dimensiones del contenedor (imagen + padding)
  const containerWidth = imgWidth + cellPadding * 2;
  const containerHeight = imgHeight + cellPadding * 2;
  // Centrar la imagen dentro del ancho máximo disponible
  const xOffset = (maxWidth - containerWidth) / 2;

  // Dibujar la imagen en el documento
  doc.addImage(
    imageBase64,
    "JPEG",
    x + xOffset + cellPadding,
    y + cellPadding,
    imgWidth,
    imgHeight,
  );

  // Agregar descripción centrada debajo (si se proporciona)
  const descriptionHeight = description ? 15 : 0;
  if (description) {
    doc.setFontSize(8);
    doc.text(
      description.length > 40
        ? description.substring(0, 37) + "..."
        : description,
      x + maxWidth / 2,
      y + containerHeight + 8,
      { align: "center" },
    );
    doc.setFontSize(10);
  }

  // Retornar la altura total ocupada (para actualizar la posición Y)
  return containerHeight + descriptionHeight;
};
